/**
 * 签约前营销方案 · 工单详情（2026-10-20 用户需求）。
 *
 * 需求拆解（用户原话要点）
 * ----------------------
 * 1. 详情只保留两块核心内容：客户/用户基础信息；CRM 中用户信息展开后的内容
 *    （现场调研、客户需求、销售进程），以实际 CRM 数据结构为准。
 * 2. CRM 信息直接复用现有只读数据，**不要在详情里二次折叠** —— 进入详情即看到展开后的完整内容。
 * 3. 提交 / 完成 / 待改进操作单独放在「工单操作区」，不与 CRM 只读信息混在一起。
 * 4. 已签约项目仍可查看完整签约前方案记录，但只读。
 * 5. 展示方案版本记录 v1/v2/v3 连续编号，不覆盖旧版本；每版含版本号、本轮要求完成时间、
 *    提交人、提交时间、实际完成时间、完整附件、本轮状态、改进要求或客户反馈。
 *
 * 待改进记录（2026-10-20 第二轮，用户原话）
 * ----------------------------------------
 * 「销售修改为待改进需要写对应的改进需求和下一次需要的时间……这些记录都需要在详情中显示处理
 *   然后状态修改为待改进……设计师会看到待改进以后又去修改，修改后提交将状态修改成已完成」
 *
 * 因此详情里除了「工单动作流水」，还多了一块**改进要求记录**（逐轮）：
 *   · 每轮显示：第几轮改进、改进需求原文、下一次要求完成时间、提出人 / 提出时间；
 *   · 数据源是工单上的 `improveRequirement / improveDueAt / improveBy / improveAt`
 *     （`/rework` 写入）；逐轮历史同时存在于版本记录的 `reworkReason / reworkDueAt` 上，
 *     版本卡片里也会再展示一次，因为设计师看版本时最关心「这版是照着哪条意见改的」；
 *   · 只读：这块是留痕，不提供编辑入口 —— 要写新意见走操作区（或列表行内「改为待改进」）。
 *
 * 改进佐证附件（2026-10-22 用户需求「列表和详情待改进点击时可以选上传佐证」）
 * ---------------------------------------------------------------------------
 * 「改为待改进」时销售可上传客户微信截图 / 现场照片 / 竞品报价（选填），三处展示：
 *   ① 操作区「提交方案」卡的待改进提示里（`order.improveFiles`，设计师改稿前直接看）；
 *   ② 「改进要求记录」每一轮（`history[].files`，逐轮留痕）；
 *   ③ 版本卡片（`v.reworkFiles`，回看这一版被哪张图退回）。
 * 三种来源都是同一批附件记录，交互统一走 `EvidenceFiles`（图片 / PDF 可预览，其余下载）。
 *
 * 布局选择
 * -------
 * 桌面用两栏：左栏「客户信息 + CRM 只读展开 + 版本记录」，右栏「工单操作区」吸顶。
 * 窄屏（<1024px）自动变单栏，操作区排在最前（设计师打开就要能上传 / 标记完成）。
 *
 * 抽屉化（2026-10-20 第二轮，用户要求「详情使用抽屉展示，参考已签约用户详情」）
 * ------------------------------------------------------------------------------
 * 参照 `pages/CRMSignedCustomers.jsx` 的做法：`/design/presign/:lid` 不再渲染独立整页，
 * 而是**和列表页渲染同一个组件**，`:lid` 存在时在列表之上叠一层 `CrmDetailDrawer`。
 * 这样得到的收益与已签约客户详情完全一致：
 *   · 关掉抽屉后用户还停在原来的列表位置与筛选条件上（筛选状态都在 URL search 里）；
 *   · 抽屉可拖宽 / 双击复位 / 宽度按账号记忆；手机自动满宽、Esc 与点遮罩都能关；
 *   · 直达链接（通知铃铛、分享）依然是「列表 + 抽屉」，可刷新、可后退。
 *
 * 因此本组件新增两个可选 props（不影响任何既有调用方）：
 *   · embedded：处于抽屉中。抽屉自带标题栏与关闭按钮，故不再渲染页面级 `V3Header`
 *     里那枚「返回列表」（重复入口），错误态的兜底出口改为关闭抽屉。
 *   · onClose：关闭抽屉（由列表页把 URL 切回 /design/presign 并还原筛选）。
 *     刻意不用 nav(-1)：筛选存在 URL search，回列表必须带上筛选，nav(-1) 不保证。
 *
 * 权限
 * ----
 * 全部写操作以后端 `permissions` 为准（canMake / canSales / readOnly），
 * 前端只负责「不该给入口的地方不给入口」，不做第二套权限判断。
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "../../../components/ui/sonner";
import { Button } from "../../../components/ui/button";
import { useApp } from "../../../context/AppContext";
import { V3Header, Denied } from "./V3Header";
import { useV3Meta, fmtD } from "./meta";
import { T } from "../../../lib/i18n";
import { fmtCN, CompleteWithFilesDialog } from "./DesignPresign";
import { uploadChunked } from "../../../lib/upload";
import { DESIGN_UPLOAD_ACCEPT } from "../../../lib/uploadAccept";
import { downloadFile } from "../../../lib/fileDownload";
import { authStore } from "../../../lib/api";
import { MediaLightbox, mediaKind } from "../../../components/MediaLightbox";
import FileUploadDate, { uploadDateLabel } from "./FileUploadDate";
/* 【2026-10-30 输入侧时区提示】「下达任务」的要求完成时间不像「改进需求」那样在 label
   里写了「洛杉矶时间」，中国同事容易按北京日期填，这里补 compact 提示。 */
import { UsDateHint } from "./UsDateHint";
import SurveyIntakeMirror from "../../../components/SurveyIntakeMirror";
import SalesProcessMirror from "../../../components/SalesProcessMirror";
import PresignRequirementDiscussion from "./PresignRequirementDiscussion";
import {
  ArrowLeft, Loader2, Upload, CheckCircle2, Undo2, Paperclip, Download, Eye, X,
  Phone, Mail, MapPin, User, FileText, History, AlertTriangle, ShieldCheck, Send, Settings2, Ban, RotateCcw,
} from "lucide-react";

const STATUS_STYLE = {
  pending_assign: "bg-slate-100 text-slate-600 border-slate-200",
  pending_make: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rework: "bg-orange-50 text-orange-700 border-orange-200",
  // 已关闭（2026-10-22）：中性灰，与列表页签保持一致（关闭不是错误状态）。
  closed: "bg-slate-100 text-slate-500 border-slate-300",
  signed: "bg-violet-50 text-violet-700 border-violet-200",
};

/** 工单状态的中文名：恢复卡片要告诉用户「恢复到哪一档」，
 *  后端 `closedFromStatus` 下发的是原始 key（pending_make 等），
 *  直接渲染会露出英文枚举，必须在这里翻成中文。 */
const STATUS_ZH = {
  pending_assign: "待分配",
  pending_make: "待制作",
  completed: "已完成",
  rework: "待改进",
  closed: "已关闭",
  signed: "已签约",
};

/** 版本状态徽标：用 v1/v2 的实际状态描述「这一版后来怎么样了」。 */
const VERSION_STATUS = {
  completed: ["已完成", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  rework: ["已退回待改进", "bg-orange-50 text-orange-700 border-orange-200"],
  signed: ["已签约留痕", "bg-violet-50 text-violet-700 border-violet-200"],
};

const fileUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

/** 改进佐证附件（2026-10-22 用户需求「待改进点击时可以选上传佐证」）。
 *
 *  销售「改为待改进」时上传的客户微信截图 / 现场照片 / 竞品报价等。
 *  三处会用到它：详情待改进提示卡（最新一轮）、改进要求记录（逐轮）、版本卡片（这一版被哪张图退回）。
 *  交互与版本附件一致：图片 / PDF / Office 点开走项目统一灯箱，CAD / CDR 等只能下载。
 */
const EvidenceFiles = ({ files, onPreview, tid }) => {
  if (!files || files.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={tid}>
      {files.map((f, i) => (
        <div key={f.fileId || i}
          className="max-w-full rounded-md border border-orange-200 bg-white px-1.5 py-1 text-[11px] text-orange-800">
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => onPreview(files, i)} title={f.filename}
              data-testid={`${tid}-open-${i}`}
              className="inline-flex min-w-0 items-center gap-1 hover:text-orange-600">
              <Paperclip className="h-3 w-3 shrink-0" />
              <span className="truncate max-w-[160px]">{f.filename || "附件"}</span>
            </button>
            <button type="button"
              onClick={() => downloadFile(f.fileId, f.filename || "附件").catch(() => toast.error(T("下载失败")))}
              aria-label={T("下载 {0}", [f.filename || "附件"])}
              data-testid={`${tid}-download-${i}`}
              className="p-0.5 text-slate-400 hover:text-orange-700"><Download className="h-3 w-3" /></button>
          </div>
          <FileUploadDate value={f.uploadedAt} className="block pl-4 leading-tight" />
        </div>
      ))}
    </div>
  );
};

/** 单个版本卡片：完整附件按组展示（一版可含平面图 / 效果图 / PPT / PDF / CAD / CDR）。 */
const VersionCard = ({ v, index, onPreview }) => {
  const [open, setOpen] = useState(index === 0);
  const files = v.files || [];
  const [label, cls] = VERSION_STATUS[v.status] || ["已完成", "bg-slate-100 text-slate-600 border-slate-200"];
  return (
    <div className="min-w-0 rounded-lg border border-slate-200" data-testid={`dv3-presign-version-${v.version}`} data-status={v.status || ""}>
      <button type="button" onClick={() => setOpen((x) => !x)}
        className="flex min-h-11 w-full flex-wrap items-center gap-2 px-3 py-2 text-left md:min-h-0"
        data-testid={`dv3-presign-version-toggle-${v.version}`}
        aria-expanded={open}>
        <span className="inline-flex items-center rounded-md bg-slate-900 px-1.5 py-0.5 font-mono text-[11px] font-bold text-white">v{v.version}</span>
        <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{T(label)}</span>
        <span className="text-[11px] text-slate-500">{T("要求完成")} {fmtCN(v.dueAt, false)}</span>
        <span className="text-[11px] text-slate-400">·</span>
        <span className="text-[11px] text-slate-500">{T("提交人")} {v.submittedBy || "—"}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-400">
          <Paperclip className="h-3 w-3" />{files.length}
        </span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 px-3 py-2">
          <div className="grid grid-cols-1 gap-1.5 text-[11px] text-slate-600 sm:grid-cols-2">
            <p data-testid={`dv3-presign-version-due-${v.version}`}><span className="text-slate-400">{T("本轮要求完成时间")}：</span>{fmtCN(v.dueAt, false)}</p>
            <p><span className="text-slate-400">{T("提交时间")}：</span>{fmtCN(v.submittedAt)}</p>
            <p data-testid={`dv3-presign-version-actual-${v.version}`}><span className="text-slate-400">{T("实际完成时间")}：</span>{fmtCN(v.actualFinishAt)}</p>
            <p><span className="text-slate-400">{T("实际完成人")}：</span>{v.completedBy || v.submittedBy || "—"}</p>
          </div>
          {/* 这一版被退回时的改进要求。同时写出「下一次要求完成时间」——
              设计师回看旧版本时，最需要知道的是「当时要求我什么时候改完」，
              只给原因不给时间，事后无法判断是否延期。 */}
          {v.reworkReason && (
            <div className="space-y-1 rounded-md bg-orange-50 px-2 py-1.5 text-[11px] text-orange-800"
              data-testid={`dv3-presign-version-reason-${v.version}`}>
              <p><span className="font-semibold">{T("改进要求 / 客户反馈")}：</span>{v.reworkReason}</p>
              {v.reworkDueAt && (
                <p data-testid={`dv3-presign-version-rework-due-${v.version}`}>
                  <span className="font-semibold">{T("当时下一次要求完成时间")}：</span>{fmtCN(v.reworkDueAt, false)}
                </p>
              )}
              {(v.reworkBy || v.reworkAt) && (
                <p className="text-orange-700/80">
                  {T("提出人")}：{v.reworkBy || "—"}{v.reworkAt ? ` · ${fmtCN(v.reworkAt)}` : ""}
                </p>
              )}
              {/* 这一版被退回时销售上传的佐证（客户截图等），回看旧版时一并可见 */}
              <EvidenceFiles files={v.reworkFiles} onPreview={onPreview}
                tid={`dv3-presign-version-rework-files-${v.version}`} />
            </div>
          )}
          {v.note && <p className="text-[11px] text-slate-600"><span className="text-slate-400">{T("提交说明")}：</span>{v.note}</p>}
          {files.length === 0 ? (
            <p className="text-[11px] text-slate-400">{T("本版没有留存附件（历史记录）")}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid={`dv3-presign-version-files-${v.version}`}>
              {files.map((f, i) => (
                <span key={f.fileId || i}
                  className="inline-flex max-w-full flex-col items-start gap-0.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">
                  <span className="inline-flex max-w-full items-center gap-1">
                  <button type="button" onClick={() => onPreview(files, i)} title={f.filename}
                    data-testid={`dv3-presign-file-preview-${v.version}-${i}`}
                    className="inline-flex min-w-0 items-center gap-1 hover:text-violet-700">
                    <Eye className="h-3 w-3 shrink-0" />
                    {f.label && <span className="shrink-0 rounded bg-slate-100 px-1 text-[10px] text-slate-500">{f.label}</span>}
                    <span className="truncate max-w-[180px]">{f.filename || f.name || "附件"}</span>
                  </button>
                  <button type="button" onClick={() => downloadFile(f.fileId, f.filename || f.name || "附件").catch(() => toast.error(T("下载失败")))}
                    data-testid={`dv3-presign-file-download-${v.version}-${i}`}
                    aria-label={T("下载 {0}", [f.filename || "附件"])}
                    className="p-1 text-slate-400 hover:text-violet-700"><Download className="h-3 w-3" /></button>
                  </span>
                  <FileUploadDate value={f.uploadedAt} className="block leading-tight" />
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function DesignPresignDetail({ embedded = false, onClose, onChanged } = {}) {
  const { lid } = useParams();
  const nav = useNavigate();
  const { api, lang } = useApp();
  const meta = useV3Meta();
  const zh = lang === "zh";
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lb, setLb] = useState(null);
  const [completedMode, setCompletedMode] = useState(null);
  const [designers, setDesigners] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data } = await api.get(`/design/presign/orders/${lid}`);
      setD(data);
    } catch (e) {
      setErr(e?.response?.status || 0);
    }
  }, [api, lid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/design/presign/designers").then(({ data }) => setDesigners(data.designers || [])).catch(() => {});
  }, [api]);

  // 操作区表单状态
  const [dueAt, setDueAt] = useState("");
  const [requirement, setRequirement] = useState("");
  const [designer, setDesigner] = useState("");
  const [reworkDue, setReworkDue] = useState("");
  const [reworkReason, setReworkReason] = useState("");
  const [reworkDesigner, setReworkDesigner] = useState("");
  // 佐证附件（2026-10-22 用户需求「列表和详情待改进点击时可以选上传佐证」）：
  // 与列表行内弹窗、以及「关闭工单」三处同一套口径 —— 需求/时间必填、佐证选填、一次提交。
  const [reworkFiles, setReworkFiles] = useState([]);
  const reworkFileRef = useRef(null);
  const [note, setNote] = useState("");
  const [doneNote, setDoneNote] = useState("");
  // 关闭工单（2026-10-22）：理由必填、附件可选，与列表行内弹窗同一套字段与接口。
  const [closeReason, setCloseReason] = useState("");
  const [closeFiles, setCloseFiles] = useState([]);
  const closeFileRef = useRef(null);

  useEffect(() => {
    if (!d) return;
    setDueAt(d.order?.dueAt || "");
    setRequirement(d.order?.requirement || "");
    // 新工单没有负责人时，直接选中后端算出的默认负责人，
    // 让「项目负责人 / 联系电话 / 邮箱」在销售还没动手时就先显示出来（需求三.4）。
    setDesigner(d.order?.designer || d.defaultDesigner || "");
    setReworkDue(d.order?.dueAt || "");
    setReworkDesigner("");
    setDoneNote("");
  }, [d?.order?.leadId, d?.order?.dueAt, d?.order?.designer, d?.order?.requirement, d?.defaultDesigner]); // eslint-disable-line

  const order = d?.order;
  const perms = d?.permissions || {};
  // 负责人展示口径：已有负责人 → 工单负责人；还没下达 → 后端带出的默认负责人。
  // 两种情况都要显示姓名 / 电话 / 邮箱（需求三.4：销售创建时就要看到负责人联系方式）。
  const showDesigner = d?.contacts?.designer || d?.defaultContact || null;
  const versions = useMemo(() => (d?.versions || []).slice().sort((a, b) => (b.version || 0) - (a.version || 0)), [d]);
  const draftFiles = d?.draftFiles || [];
  /**
   * 改进要求逐轮记录（2026-10-20 第二轮需求：「这些记录都需要在详情中显示处理」）。
   *
   * 口径：以工单动作流水里的 rework 动作为**唯一真源**，按时间正序重排成第 1/2/3 轮。
   * 为什么不直接用 `order.improveRequirement`：那只保留最近一次，客户提两轮意见时
   * 详情就只剩最后一条，「上一轮为什么退回」会丢 —— 而版本记录里每版的 reworkReason
   * 是按版本留痕的，两处合起来才能对上「第 N 轮意见 → vN 版本」。
   * 这里用 history 而不是 versions，是因为 history 在还没交新版时就已有记录
   * （销售刚点完待改进、设计师还没提交的那一刻，详情也应该立刻显示这条要求）。
   */
  const improveRounds = useMemo(() => {
    const list = (order?.history || []).filter((h) => h?.action === "rework");
    return list.map((h, i) => ({ ...h, round: i + 1 }));
  }, [order?.history]);
  const canSales = !!perms.canSales;
  const canMake = !!perms.canMake;
  const canRevise = !!perms.canRevise;
  const readOnly = !!perms.readOnly;

  /**
   * 操作区只显示**一张**与当前状态匹配的卡片（2026-10-21 用户反馈）。
   *
   * 反馈现象：admin 这类同时具备销售 + 设计权限的账号打开详情，会同时看到
   * 「下达 / 更新任务」「提交方案」「改为待改进」三张卡，不知道现在该点哪张，
   * 也和列表里「一行只给一个可执行动作」的口径不一致。
   *
   * 口径：按**角色 × 工单状态**给卡，与列表 canComplete / canImprove 同源：
   *   ① 已签约           → 不给任何操作卡（只读留痕，另有只读提示卡）
   *   ② 待分配（没负责人）→ 销售：下达任务（设计师此时无事可做）
   *   ③ 待制作           → 设计师：提交方案（销售此时只能等）
   *   ④ 待改进           → 设计师：提交方案 + 销售：改为待改进（可补充新一轮意见）
   *   ⑤ 已完成           → 设计师：重新提交或增加附件；销售：改为待改进
   * 纯角色账号（销售 或 设计）最多只出现一张；admin 同时具备销售与设计权限时，
   * 在「待改进」或「已完成」可能看到两张，分别对应不同角色的实际动作。
   */
  // 每个状态该由谁动手（与列表 canComplete / canImprove 同源）：
  //   待分配 → 销售下达；待制作 → 设计师提交；待改进 → 设计师改 + 销售可补充意见；
  //   已完成 → 设计师补交或销售提改进；已签约 → 谁都不动手。
  const status = order?.status;
  const showDispatch = canSales && !readOnly && status === "pending_assign";
  const showMake = canMake && (status === "pending_make" || status === "rework");
  // 「改为待改进」入口一律听后端的 `permissions.canImprove`（2026-10-22 修复）。
  //
  // 原来这里硬编码 `status === "completed" || status === "rework"`，与列表的
  // `canImprove` 各算一套，于是被旧逻辑误写成 pending_make 的返工单
  // （线上 ADU-0149 / ADU-0150：设计师 10-19 就交过一版，销售 10-21 重新下达
  // 时状态被写成待制作）在列表上有「改为待改进」按钮，点进详情却整块卡片消失，
  // 销售根本找不到退回入口 —— 用户描述成「销售让重新修改了，但详情里没有输入框」。
  //
  // 后端现在按「状态是 completed/rework **或** 确实交过方案」下发 canImprove，
  // 与列表、/rework 接口三处同源。前端不再自己判断状态，只兜底老响应缺字段的情况
  // （canImprove 为 undefined 时才回退到原状态判断，避免接口未升级时按钮全消失）。
  const canImproveFlag = perms.canImprove;
  const showImprove = canImproveFlag === undefined
    ? (canSales && !readOnly && (status === "completed" || status === "rework"))
    : (!!canImproveFlag && !readOnly);
  // admin 这类同时具备销售 + 设计权限的账号，在「待改进」下会同时拿到
  // 提交方案（设计师步）与改为待改进（销售步）两张卡 —— 这是允许的上限（最多两张）；
  // 纯角色账号也只会拿到属于自己的那一张。
  const [showDispatchMore, setShowDispatchMore] = useState(false);
  // 已关闭（2026-10-22）：不再给下达 / 提交 / 待改进，只留「恢复」。
  // 权限一律听后端 permissions.canClose / canRestore（与列表行级同源），
  // 前端不自己拼角色判断 —— 否则又会出现「详情有按钮、接口 400」。
  const isClosed = status === "closed";
  const canClose = !!perms.canClose;
  const canRestore = !!perms.canRestore;
  const needsRedispatch = canSales && !readOnly && !isClosed && status !== "pending_assign" && !showDispatchMore;

  /** 打开预览：图片 / PDF / Office 走项目统一灯箱；其余格式（CAD / CDR）只能下载。 */
  const preview = (files, index) => {
    const items = files.map((f) => ({
      id: f.fileId, url: fileUrl(f.fileId), kind: mediaKind(f.filename || "", f.contentType || ""),
      name: f.filename || f.name, meta: [f.label, uploadDateLabel(f.uploadedAt)].filter(Boolean).join(" · "),
    }));
    const target = items[index];
    if (!target || target.kind === "file") {
      downloadFile(files[index].fileId, files[index].filename || "附件").catch(() => toast.error(T("下载失败")));
      return;
    }
    setLb({ items, index });
  };

  const dispatch = async () => {
    if (!dueAt) { toast.error(T("请填写要求完成时间后再发送任务")); return; }
    setBusy(true);
    try {
      await api.post(`/design/presign/orders/${lid}/dispatch`, { dueAt, requirement, designer: designer || undefined });
      toast.success(T("任务已下达，设计师已收到提醒"));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("label", "");
        await api.post(`/design/presign/orders/${lid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      }
      toast.success(T("已上传 {0} 个附件（还需点「标记为已完成」才算完成）", [ok]));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || T("上传失败"));
      if (ok) await load();
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeDraft = async (fid) => {
    setBusy(true);
    try { await api.delete(`/design/presign/orders/${lid}/draft/${fid}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/design/presign/orders/${lid}/complete`, { note: doneNote });
      toast.success(T("已标记完成，生成 v{0}", [data.version]));
      setDoneNote("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const rework = async () => {
    if (!reworkDue) { toast.error(T("请填写下一次要求完成时间")); return; }
    if (!reworkReason.trim()) { toast.error(T("请填写改进需求")); return; }
    setBusy(true);
    try {
      // multipart：改进需求 / 下一次时间 / 负责人 + 可选佐证附件一次提交，
      // 附件与这一轮退回记录绑定（后端写进 history[].files 与 versions[-1].reworkFiles）。
      const fd = new FormData();
      fd.append("dueAt", reworkDue);
      fd.append("reason", reworkReason.trim());
      if (reworkDesigner) fd.append("designer", reworkDesigner);
      reworkFiles.forEach((f) => fd.append("files", f));
      await api.post(`/design/presign/orders/${lid}/rework`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("已改为待改进，设计师已收到提醒；要求完成时间已更新为 {0}", [reworkDue]));
      setReworkReason("");
      setReworkFiles([]);
      if (reworkFileRef.current) reworkFileRef.current.value = "";
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const reassign = async () => {
    if (!designer) { toast.error(T("请选择新的营销方案设计师")); return; }
    setBusy(true);
    try {
      await api.post(`/design/presign/orders/${lid}/reassign`, { designer });
      toast.success(T("负责人已更新"));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  /**
   * 关闭工单（2026-10-22 用户需求）：
   * 「设计师可以主动点击关闭，点击是确认，并让写关闭理由或上传附件（附件可选）」。
   * 理由必填、附件可选，一次 multipart 请求提交；关闭后工单从待办与催办中移出，
   * 版本与附件全部保留，随时可恢复。
   */
  const closeOrder = async () => {
    if (!closeReason.trim()) { toast.error(T("请填写关闭理由")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reason", closeReason.trim());
      closeFiles.forEach((f) => fd.append("files", f));
      await api.post(`/design/presign/orders/${lid}/close`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("工单已关闭，已从待办与催办中移出；客户回心转意时可在列表恢复"));
      setCloseReason(""); setCloseFiles([]);
      if (closeFileRef.current) closeFileRef.current.value = "";
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  /** 恢复已关闭工单：回到关闭前的状态（后端 closedFromStatus 决定，前端不猜）。 */
  const restoreOrder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/design/presign/orders/${lid}/restore`);
      toast.success(T("已恢复为「{0}」", [data.order?.statusLabel || T("待制作")]));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  if (!meta) return <div className="h-64 animate-pulse rounded-lg border border-slate-200 bg-white" data-testid="dv3-presign-detail-loading" />;
  if (meta.error) return <Denied status={meta?.status} />;
  if (err === 403) return <Denied status={403} />;
  if (err) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-center" data-testid="dv3-presign-detail-error">
        <p className="text-sm text-slate-600">{T("工单加载失败或不存在")}</p>
        {/* 抽屉态没有页面内的「返回列表」按钮位（抽屉自带关闭），故兜底出口改为关闭抽屉 */}
        <Button size="sm" variant="outline" className="mt-3" data-testid="dv3-presign-error-back"
          onClick={() => (embedded && onClose ? onClose() : nav("/design/presign"))}>{T("返回列表")}</Button>
      </div>
    );
  }
  if (!d) return <div className="space-y-3" data-testid="dv3-presign-detail-skeleton"><div className="h-24 animate-pulse rounded-lg bg-white" /><div className="h-64 animate-pulse rounded-lg bg-white" /></div>;

  const stCls = STATUS_STYLE[order.status] || STATUS_STYLE.pending_assign;
  const customer = d.customer || {};

  return (
    <div className="space-y-3" data-testid="dv3-presign-detail" data-lead={order.leadId} data-status={order.status} data-embedded={embedded ? "1" : "0"}>
      {/* 抽屉态不渲染页面级头部：抽屉自带标题栏 + 右上角关闭（另有 Esc / 点遮罩），
          再来一个「返回列表」就是重复入口 —— 与已签约客户详情嵌入抽屉时的处理一致。 */}
      {!embedded && (
        <V3Header title={T("营销方案工单")} right={
          <Button size="sm" variant="outline" data-testid="dv3-presign-back" onClick={() => nav("/design/presign")}>
            <ArrowLeft className="h-4 w-4" />{T("返回列表")}
          </Button>
        } />
      )}

      {/* 头部：实体身份 + 状态 + 关键数字（首屏落点） */}
      <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="dv3-presign-head">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-bold text-slate-900" data-keep-title data-testid="dv3-presign-head-address">{order.address || "—"}</h2>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {order.projectId || "—"} · {order.clientName || "—"}{order.city ? ` · ${order.city}` : ""}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${stCls}`}
            data-testid="dv3-presign-head-status" data-status={order.status}>
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />{T(order.statusLabel)}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {[
            [T("销售负责人"), order.salesName || "—"],
            [T("营销方案设计师"), order.designerName || order.designer || T("未分配")],
            [T("要求完成时间（洛杉矶时间）"), fmtCN(order.dueAt, false)],
            [T("实际完成时间"), fmtCN(order.actualFinishAt)],
            [T("当前版本"), order.version ? `v${order.version}` : "—"],
          ].map(([k, v]) => (
            <div key={k} className="min-w-0 rounded-md bg-slate-50 px-2 py-1.5">
              <p className="text-[10px] text-slate-400">{k}</p>
              <p className="truncate text-xs font-semibold text-slate-700" title={String(v)}>{v}</p>
            </div>
          ))}
        </div>
        {readOnly && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-violet-50 px-2 py-1 text-[11px] text-violet-700" data-testid="dv3-presign-readonly-note">
            <ShieldCheck className="h-3.5 w-3.5" />{T("项目已签约，以下记录为只读留痕，历史任务与附件不可修改。")}
          </p>
        )}
        {/* 已关闭提示（2026-10-22）：进入详情第一眼就要知道「这单不做了」，
            否则看到「待制作」时期的要求完成时间会以为还要交方案。 */}
        {isClosed && !readOnly && (
          <div className="mt-2 rounded-md bg-slate-100 px-2.5 py-2 text-[11px] leading-5 text-slate-700"
            data-testid="dv3-presign-closed-banner">
            <p className="inline-flex items-center gap-1.5 font-semibold text-slate-800">
              <Ban className="h-3.5 w-3.5" />{T("本工单已关闭")}
            </p>
            <p className="mt-0.5">
              {T("客户不需要该方案，本单已从待办、设计师工作台与逾期催办中移出。")}
              {order.closedReason ? <>{T("关闭理由")}：{order.closedReason}</> : null}
            </p>
            {order.closedBy && (
              <p className="text-slate-500">{T("关闭人")} {order.closedBy} · {fmtCN(order.closedAt)}</p>
            )}
            <p className="text-slate-500">{T("客户回心转意时，可在右侧「恢复工单」回到关闭前的状态。")}</p>
          </div>
        )}
      </section>

      {/* 布局栅格：外层的 .presign-detail-wrap 只声明 container-type，.presign-detail-grid
          由 @container 规则决定单栏 / 双栏（见 index.css 末尾）。为什么不用 lg: 视口断点：
          详情现在住在抽屉里，抽屉宽度 ≠ 视口宽度 —— 1440 视口把抽屉拖到 600px 时，
          视口断点仍给「右栏 360px」，左栏只剩 200px 挤成一缕。容器查询只跟抽屉实际宽度走。
          单栏时的阅读顺序由下面两个子容器的 order-* 控制：抽屉态操作区在前（设计师打开即能上传）。 */}
      <div className="presign-detail-wrap">
        <div className="presign-detail-grid">
          {/* 左栏：客户信息 + CRM 只读展开 + 版本记录 */}
          <div className="presign-detail-main space-y-3">
            {/* ① 客户 / 用户基础信息 */}
            <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="dv3-presign-customer">
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                <User className="h-4 w-4 text-violet-500" />{T("客户基础信息")}
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
                {[
                  [T("客户"), customer.clientName],
                  [T("项目编号"), order.projectId],
                  [T("项目地址"), customer.address || order.address],
                  [T("城市"), customer.city],
                  [T("电话"), customer.phone],
                  [T("邮箱"), customer.email],
                  [T("销售负责人"), customer.assignedSales || order.salesName],
                  [T("服务类型"), customer.serviceType],
                ].map(([k, v]) => (
                  <div key={k} className="flex min-w-0 gap-1.5">
                    <dt className="shrink-0 text-slate-400">{k}：</dt>
                    <dd className="min-w-0 break-words text-slate-700">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-2 grid grid-cols-1 gap-1.5 rounded-md bg-slate-50 p-2 text-[11px] text-slate-600 sm:grid-cols-2"
                data-testid="dv3-presign-designer-contact">
                {/* 新工单还没下达时 showDesigner 兜到默认负责人，避免这块空白让人以为「没有负责人」 */}
                <p className="font-semibold text-slate-700">{T("项目负责人")}：{showDesigner?.name || "—"}</p>
                <p className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" />{T("联系电话")}：{showDesigner?.phone || T("未登记")}</p>
                <p className="inline-flex items-center gap-1 sm:col-span-2"><Mail className="h-3 w-3 text-slate-400" />{T("邮箱")}：{showDesigner?.email || T("未登记")}</p>
              </div>
            </section>

            {/* ② CRM 用户信息展开后的内容（只读，不折叠 —— 进入详情即见全量） */}
            <section className="min-w-0 space-y-2" data-testid="dv3-presign-crm">
              <h3 className="flex items-center gap-1.5 px-1 text-sm font-semibold text-slate-800">
                <MapPin className="h-4 w-4 text-violet-500" />{T("CRM 客户资料（只读）")}
                <span className="text-[10px] font-normal text-slate-400">{T("现场调研 · 客户需求 · 销售进程")}</span>
              </h3>
              {customer.leadId ? (
                <>
                  <SurveyIntakeMirror api={api} leadId={customer.leadId} zh={zh} canEdit={false} />
                  {/* 只读镜像（2026-10-20 修正）：CRM 资料块对设计角色与已签约工单都必须只读。
                      原先 SalesProcessMirror 不支持只读，这里只能靠外层包裹层做提示，
                      输入框实际仍可编辑（设计师能在别人的客户里新建评估记录），与需求六冲突。
                      现在组件支持 readOnly，新增表单 / 附件增删 / 布局绘制入口全部隐藏，
                      只保留已有记录与附件浏览。 */}
                  <div className="relative min-w-0">
                    <SalesProcessMirror api={api} leadId={customer.leadId} zh={zh} readOnly />
                    <p className="mt-1 text-[10px] text-slate-400">{T("销售进程由销售维护，这里仅供查看。")}</p>
                  </div>
                </>
              ) : (
                <p className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500">
                  {T("该客户的 CRM 线索已归档，无法展示现场调研与销售进程；方案记录仍可查看。")}
                </p>
              )}
            </section>

            {/* ③ 版本记录 */}
            <section className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dv3-presign-versions">
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                <History className="h-4 w-4 text-violet-500" />{T("方案版本记录")}
                <span className="text-[10px] font-normal text-slate-400">{T("共 {0} 版（v1 起连续编号，旧版保留）", [versions.length])}</span>
              </h3>
              {versions.length === 0 ? (
                <p className="py-4 text-center text-xs text-slate-400" data-testid="dv3-presign-versions-empty">{T("尚无提交版本")}</p>
              ) : (
                <div className="space-y-2">
                  {versions.map((v, i) => <VersionCard key={v.version} v={v} index={i} onPreview={preview} />)}
                </div>
              )}
            </section>

            {/* ④ 改进要求记录（逐轮）：销售每次「改为待改进」写下的意见 + 下一次要求完成时间。
                这块是设计师改稿的依据，也是事后核对「是否按期」的凭据，所以放在版本记录下方、
                与只读信息同栏；它属于留痕，不提供编辑入口（新意见走操作区 / 列表行内）。 */}
            <section className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dv3-presign-improvements">
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                <Undo2 className="h-4 w-4 text-orange-500" />{T("改进要求记录")}
                <span className="text-[10px] font-normal text-slate-400">
                  {T("共 {0} 轮（销售提出，设计师按此修改）", [improveRounds.length])}
                </span>
              </h3>
              {improveRounds.length === 0 ? (
                <p className="py-3 text-center text-xs text-slate-400" data-testid="dv3-presign-improvements-empty">
                  {T("客户尚未提出改进要求。销售在列表或操作区点「改为待改进」后会记录在这里。")}
                </p>
              ) : (
                <ol className="space-y-2" data-testid="dv3-presign-improvements-list">
                  {improveRounds.map((h, i) => (
                    <li key={`${h.at || ""}-${i}`} className="rounded-md border border-orange-100 bg-orange-50/50 px-2.5 py-2"
                      data-testid={`dv3-presign-improvement-${i}`}>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                        <span className="rounded bg-orange-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {T("第 {0} 轮", [h.round])}
                        </span>
                        <span className="text-slate-500">{T("下一次要求完成时间")}：
                          <span className="font-semibold tabular-nums text-orange-800">{fmtCN(h.dueAt, false)}</span>
                        </span>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500">{T("提出人")} {h.by || "—"}</span>
                        <span className="text-slate-400">·</span>
                        <span className="tabular-nums text-slate-400">{fmtCN(h.at)}</span>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-5 text-slate-700">
                        {h.reason || "—"}
                      </p>
                      {/* 这一轮销售上传的佐证（客户截图 / 现场照片等），有才显示 */}
                      <EvidenceFiles files={h.files} onPreview={preview}
                        tid={`dv3-presign-improvement-files-${i}`} />
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>

          {/* 右栏：工单操作区（与 CRM 只读信息完全分开） */}
          <aside className="presign-detail-aside space-y-3" data-testid="dv3-presign-actions">
            {/* 方案要求不再只存在于销售下达表单：双方共用原要求、追加记录与留言，
                补充不经过重新下达，避免把讨论误当返工或修改工期。 */}
            <PresignRequirementDiscussion key={lid} lid={lid} refreshKey={d} />
            {/* 销售：下达 / 更新任务
                默认只在「还没下达 / 没负责人」这一步出现（每个状态只给一张动作卡）；
                其它状态下收进「修改要求 / 重新下达」按钮，需要时才展开。 */}
            {canSales && !readOnly && (showDispatch || showDispatchMore) && (
              <section className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="dv3-presign-dispatch">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-violet-900">
                  <Send className="h-4 w-4" />{T("下达 / 更新任务")}
                </h3>
                <label className="block text-[11px] font-semibold text-slate-600">
                  {T("要求完成时间")} <span className="text-red-500">*</span>
                  <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="dv3-presign-dispatch-due"
                    className="mt-1 w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                  <span className="mt-1 block text-[10px] font-normal text-slate-400">{T("未填写不允许发送任务。")}</span>
                  <UsDateHint compact testid="dv3-presign-dispatch-tz" className="mt-1" />
                </label>
                <label className="mt-2 block text-[11px] font-semibold text-slate-600">
                  {T("方案要求")}
                  <textarea value={requirement} onChange={(e) => setRequirement(e.target.value)} rows={3}
                    data-testid="dv3-presign-dispatch-req" placeholder={T("例如：总平面图、平面图、3D 效果图、PPT 方案")}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                </label>
                <label className="mt-2 block text-[11px] font-semibold text-slate-600">
                  {T("项目负责人")}
                  <select value={designer} onChange={(e) => setDesigner(e.target.value)} data-testid="dv3-presign-dispatch-designer"
                    className="mt-1 w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 px-2 py-1.5 text-xs bg-white">
                    <option value="">{d?.defaultDesigner ? T("默认负责人：{0}", [designers.find((x) => x.username === d.defaultDesigner)?.name || d.defaultDesigner]) : T("默认负责人（自动带出）")}</option>
                    {designers.map((x) => <option key={x.username} value={x.username}>{x.name}{x.phone ? ` · ${x.phone}` : ""}</option>)}
                  </select>
                </label>
                {/* 负责人联系方式：优先看下拉当前选中的人；还没选时用后端带出的默认负责人。
                    两种情况都完整显示姓名 / 电话 / 邮箱，销售确认后再发送（需求三.4）。 */}
                {(designers.find((x) => x.username === designer) || showDesigner) && (
                  <div className="mt-2 rounded-md bg-white p-2 text-[11px] text-slate-600" data-testid="dv3-presign-dispatch-contact">
                    {(() => { const c = designers.find((x) => x.username === designer) || showDesigner; return (<>
                      <p className="font-semibold text-slate-700">{T("项目负责人")}：{c.name}</p>
                      <p>{T("联系电话")}：{c.phone || T("未登记")}</p>
                      <p>{T("邮箱")}：{c.email || T("未登记")}</p>
                    </>); })()}
                  </div>
                )}
                <Button size="sm" className="mt-2 w-full" data-testid="dv3-presign-dispatch-submit"
                  disabled={busy || !dueAt} onClick={dispatch}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {order.dispatchedAt ? T("发送任务") : T("创建并发送任务")}
                </Button>
              </section>
            )}

            {/* 设计师：上传 + 标记完成
                只在「待制作 / 待改进」时出现 —— 这两个状态下该动的就是设计师。 */}
            {showMake && (
              <section className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="dv3-presign-make">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-emerald-900">
                  <Upload className="h-4 w-4" />{T("提交方案")}
                </h3>
                {/* 待改进时的醒目提示：设计师打开详情就要看到「改什么、什么时候要」，
                    不必自己去版本记录或流水里翻。提交成功后状态会自动回到「已完成」。 */}
                {order.status === "rework" && (
                  <div className="mb-2 rounded-md border border-orange-200 bg-white px-2 py-1.5 text-[11px] leading-5"
                    data-testid="dv3-presign-make-improve-hint">
                    <p className="font-semibold text-orange-800">
                      {T("本单已改为待改进，请按以下要求修改后重新提交")}
                    </p>
                    <p className="mt-0.5 text-slate-700">
                      <span className="text-slate-400">{T("改进需求")}：</span>
                      {order.improveRequirement || order.reworkReason || "—"}
                    </p>
                    <p className="text-slate-700">
                      <span className="text-slate-400">{T("下一次要求完成时间")}：</span>
                      <span className="font-semibold tabular-nums text-orange-800">{fmtCN(order.improveDueAt || order.dueAt, false)}</span>
                    </p>
                    {/* 销售提交待改进时上传的佐证：设计师改稿前直接点开看客户截图，不用翻流水 */}
                    {(order.improveFiles || []).length > 0 && (
                      <div className="mt-1 border-t border-orange-100 pt-1">
                        <p className="text-slate-400">{T("客户佐证")}：</p>
                        <EvidenceFiles files={order.improveFiles} onPreview={preview}
                          tid="dv3-presign-make-improve-files" />
                      </div>
                    )}
                  </div>
                )}
                <input ref={fileRef} type="file" multiple className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
                  onChange={upload} data-testid="dv3-presign-upload-input" />
                <Button size="sm" variant="outline" className="w-full" data-testid="dv3-presign-upload-btn"
                  disabled={busy} onClick={() => fileRef.current?.click()}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  {T("上传方案附件（可多选）")}
                </Button>
                <p className="mt-1 text-[10px] text-slate-500">{T("支持平面图、效果图、PPT、PDF、CAD、CDR 等；同一版本可包含多个附件。")}</p>

                <div className="mt-2 space-y-1" data-testid="dv3-presign-drafts">
                  <p className="text-[11px] font-semibold text-slate-600">{T("本轮已上传 {0} 个（尚未完成）", [draftFiles.length])}</p>
                  {draftFiles.map((f, i) => (
                    <div key={f.fileId || i} className="flex items-center gap-1.5 rounded-md bg-white px-2 py-1 text-[11px] text-slate-600">
                      <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1"><span className="block truncate">{f.filename}</span><FileUploadDate value={f.uploadedAt} className="block leading-tight" /></span>
                      <button type="button" aria-label={T("移除")} data-testid={`dv3-presign-draft-remove-${i}`}
                        onClick={() => removeDraft(f.fileId)} className="p-0.5 text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {draftFiles.length === 0 && <p className="text-[10px] text-slate-400">{T("还没有上传附件")}</p>}
                </div>

                <label className="mt-2 block text-[11px] font-semibold text-slate-600">
                  {T("提交说明（可选）")}
                  <textarea value={doneNote} onChange={(e) => setDoneNote(e.target.value)} rows={2} data-testid="dv3-presign-complete-note"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                </label>
                <Button size="sm" className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700" data-testid="dv3-presign-complete"
                  disabled={busy || draftFiles.length === 0} onClick={complete}>
                  <CheckCircle2 className="h-4 w-4" />{T("标记为已完成")}
                </Button>
                {draftFiles.length === 0 && (
                  <p className="mt-1 text-[10px] text-slate-400" data-testid="dv3-presign-complete-hint">
                    {T("仅上传附件不会自动完成，请上传后手动点「标记为已完成」。")}
                  </p>
                )}
              </section>
            )}

            {/* 2026-10-26 用户需求：已完成后仍可能有新版或漏传附件。
                设计师必须先选语义：重新提交才生成 v+1；增加附件只补当前版，
                两种操作都走列表共用弹窗，附件归属和权限由后端同源约束。 */}
            {canRevise && status === "completed" && (
              <section className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="dv3-presign-completed-actions">
                <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-violet-900">
                  <Upload className="h-4 w-4" />{T("已完成方案补交")}
                </h3>
                <p className="mb-2 text-[11px] text-slate-600">{T("当前 v{0}，请选择补交方式。", [order.version])}</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Button size="sm" variant="outline" data-testid="dv3-presign-resubmit-btn"
                    onClick={() => setCompletedMode("resubmit")}>
                    <Upload className="h-4 w-4" />{T("重新提交")}
                  </Button>
                  <Button size="sm" variant="outline" data-testid="dv3-presign-append-btn"
                    onClick={() => setCompletedMode("append")}>
                    <Paperclip className="h-4 w-4" />{T("增加附件")}
                  </Button>
                </div>
              </section>
            )}

            {/* 销售：改为待改进（2026-10-20 第二轮：状态名从「重新制作」改为「待改进」，
                含义也随之明确 —— 不是整版重做，而是按销售写下的改进需求改。） */}
            {showImprove && (
              <section className="rounded-lg border border-orange-200 bg-orange-50/40 p-3" data-testid="dv3-presign-rework">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-orange-900">
                  <Undo2 className="h-4 w-4" />{T("改为待改进")}
                </h3>
                <p className="mb-2 text-[10px] leading-4 text-slate-500">
                  {T("客户对方案不满意时改为待改进。必须填写改进需求与下一次要求完成时间；旧版本与旧截止时间会完整保留。")}
                </p>
                <label className="block text-[11px] font-semibold text-slate-600">
                  {T("改进需求")} <span className="text-red-500">*</span>
                  <textarea value={reworkReason} onChange={(e) => setReworkReason(e.target.value)} rows={3} data-testid="dv3-presign-rework-reason"
                    placeholder={T("例如：客户希望后院增加露台，效果图需重做")}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                </label>
                <label className="mt-2 block text-[11px] font-semibold text-slate-600">
                  {T("下一次要求完成时间（洛杉矶时间）")} <span className="text-red-500">*</span>
                  <input type="date" value={reworkDue} onChange={(e) => setReworkDue(e.target.value)} data-testid="dv3-presign-rework-due"
                    className="mt-1 w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                </label>
                <label className="mt-2 block text-[11px] font-semibold text-slate-600">
                  {T("负责人（默认保留原负责人）")}
                  <select value={reworkDesigner} onChange={(e) => setReworkDesigner(e.target.value)} data-testid="dv3-presign-rework-designer"
                    className="mt-1 w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 px-2 py-1.5 text-xs bg-white">
                    <option value="">{T("保留原负责人")}（{order.designerName || order.designer || T("未分配")}）</option>
                    {designers.map((x) => <option key={x.username} value={x.username}>{x.name}</option>)}
                  </select>
                </label>
                {/* 佐证附件（可选）：客户截图 / 现场照片 / 竞品报价。
                    跟着这一轮改进要求一起留痕，设计师在「改进要求记录」与版本卡片里都能回看。 */}
                <div className="mt-2">
                  <input ref={reworkFileRef} type="file" multiple className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
                    onChange={(e) => {
                      const picked = Array.from(e.target.files || []);
                      if (picked.length) setReworkFiles((old) => [...old, ...picked]);
                      if (reworkFileRef.current) reworkFileRef.current.value = "";
                    }} data-testid="dv3-presign-rework-file-input" />
                  <Button size="sm" variant="outline" className="w-full" disabled={busy}
                    onClick={() => reworkFileRef.current?.click()} data-testid="dv3-presign-rework-pick">
                    <Paperclip className="h-4 w-4" />{T("佐证附件（可选）")}
                  </Button>
                  {reworkFiles.length > 0 && (
                    <ul className="mt-1.5 space-y-1" data-testid="dv3-presign-rework-files">
                      {reworkFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center gap-1.5 rounded-md bg-white px-2 py-1 text-[11px] text-slate-600">
                          <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                          <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                          <button type="button" aria-label={T("移除 {0}", [f.name])} disabled={busy}
                            onClick={() => setReworkFiles((old) => old.filter((_, idx) => idx !== i))}
                            data-testid={`dv3-presign-rework-remove-${i}`}
                            className="p-0.5 text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button size="sm" className="mt-2 w-full bg-orange-600 hover:bg-orange-700" data-testid="dv3-presign-rework-submit"
                  disabled={busy || !reworkDue || !reworkReason.trim()} onClick={rework}>
                  <Undo2 className="h-4 w-4" />{T("改为待改进")}
                </Button>
              </section>
            )}

            {/* 次操作：修改要求 / 重新下达（默认收起）
                为什么需要：把三张卡砍成一张之后，「已完成」状态只留「改为待改进」，
                销售若只想改截止时间或换设计师就没了入口。这里用一个折叠按钮兜住
                这类低频需求，既不抢主操作位置，也不至于功能缺失。 */}
            {needsRedispatch && (
              <div data-testid="dv3-presign-more-actions">
                {!showDispatchMore ? (
                  <Button size="sm" variant="outline" className="w-full"
                    data-testid="dv3-presign-more-toggle" onClick={() => setShowDispatchMore(true)}>
                    <Settings2 className="h-4 w-4" />{T("修改要求 / 重新下达")}
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" className="w-full"
                    data-testid="dv3-presign-more-collapse" onClick={() => setShowDispatchMore(false)}>
                    <X className="h-4 w-4" />{T("收起")}
                  </Button>
                )}
              </div>
            )}

            {/* 经理 / 销售：改派负责人 */}
            {!readOnly && (perms.isManager || canSales) && (
              <section className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dv3-presign-reassign">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                  <User className="h-4 w-4 text-violet-500" />{T("改派负责人")}
                </h3>
                <select value={designer} onChange={(e) => setDesigner(e.target.value)} data-testid="dv3-presign-reassign-select"
                  className="w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 px-2 py-1.5 text-xs bg-white">
                  <option value="">{T("选择营销方案设计师")}</option>
                  {designers.map((x) => <option key={x.username} value={x.username}>{x.name}{x.phone ? ` · ${x.phone}` : ""}</option>)}
                </select>
                <Button size="sm" variant="outline" className="mt-2 w-full" data-testid="dv3-presign-reassign-submit"
                  disabled={busy || !designer} onClick={reassign}>{T("确认改派")}</Button>
              </section>
            )}

            {/* 关闭 / 恢复工单（2026-10-22 用户需求）。
                关闭：客户不需要了 → 必填理由 + 可选附件；关闭后本单从待办 / 工作台 / 催办移出。
                恢复：客户又回来了 / 误关 → 一键回到关闭前的状态（后端 closedFromStatus）。
                两张卡互斥：已关闭只给恢复，未关闭只给关闭 —— 同屏不会出现矛盾的两个动作。 */}
            {!readOnly && canClose && !isClosed && (
              <section className="rounded-lg border border-slate-300 bg-slate-50/60 p-3" data-testid="dv3-presign-close-card">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                  <Ban className="h-4 w-4 text-slate-500" />{T("关闭工单")}
                </h3>
                <p className="mb-2 text-[10px] leading-4 text-slate-500">
                  {T("客户不需要这个方案了、或长期无进展时关闭。关闭后不再出现在待制作、工作台与逾期催办里，方案版本与附件全部保留，可随时恢复。")}
                </p>
                <label className="block text-[11px] font-semibold text-slate-600">
                  {T("关闭理由")} <span className="text-red-500">*</span>
                  <textarea value={closeReason} onChange={(e) => setCloseReason(e.target.value)} rows={3} disabled={busy}
                    data-testid="dv3-presign-close-reason"
                    placeholder={T("例如：客户已确定不做 ADU；客户选择了其他公司")}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                </label>
                <div className="mt-2">
                  <input ref={closeFileRef} type="file" multiple className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
                    onChange={(e) => {
                      const picked = Array.from(e.target.files || []);
                      if (picked.length) setCloseFiles((old) => [...old, ...picked]);
                      if (closeFileRef.current) closeFileRef.current.value = "";
                    }} data-testid="dv3-presign-close-file-input" />
                  <Button size="sm" variant="outline" className="w-full" disabled={busy}
                    onClick={() => closeFileRef.current?.click()} data-testid="dv3-presign-close-pick">
                    <Paperclip className="h-4 w-4" />{T("佐证附件（可选）")}
                  </Button>
                  {closeFiles.length > 0 && (
                    <ul className="mt-1.5 space-y-1" data-testid="dv3-presign-close-files">
                      {closeFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center gap-1.5 rounded-md bg-white px-2 py-1 text-[11px] text-slate-600">
                          <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                          <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                          <button type="button" aria-label={T("移除 {0}", [f.name])} disabled={busy}
                            onClick={() => setCloseFiles((old) => old.filter((_, idx) => idx !== i))}
                            data-testid={`dv3-presign-close-remove-${i}`}
                            className="p-0.5 text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button size="sm" className="mt-2 w-full bg-slate-700 hover:bg-slate-800" data-testid="dv3-presign-close-submit"
                  disabled={busy || !closeReason.trim()} onClick={closeOrder}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}{T("确认关闭")}
                </Button>
              </section>
            )}

            {!readOnly && canRestore && isClosed && (
              <section className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="dv3-presign-restore-card">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-violet-900">
                  <RotateCcw className="h-4 w-4" />{T("恢复工单")}
                </h3>
                <p className="mb-2 text-[10px] leading-4 text-slate-600">
                  {T("客户又需要方案了、或误关时可以恢复：会回到关闭前的状态（{0}），负责人与要求完成时间都保留。",
                    [STATUS_ZH[order.closedFromStatus] || T("关闭前状态")])}
                </p>
                {order.closedReason && (
                  <div className="mb-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-600"
                    data-testid="dv3-presign-closed-note">
                    <p><span className="text-slate-400">{T("关闭理由")}：</span>{order.closedReason}</p>
                    <p className="mt-0.5 text-slate-500">
                      {T("关闭人")} {order.closedBy || "—"} · {fmtCN(order.closedAt)}
                    </p>
                    {(order.closedFiles || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5" data-testid="dv3-presign-closed-files">
                        {(order.closedFiles || []).map((f, i) => (
                          <div key={f.fileId || i} className="max-w-full rounded border border-slate-200 px-1.5 py-0.5">
                            <button type="button"
                              onClick={() => downloadFile(f.fileId, f.filename || "附件").catch(() => toast.error(T("下载失败")))}
                              data-testid={`dv3-presign-closed-file-${i}`}
                              className="inline-flex max-w-full items-center gap-1 text-[10px] text-slate-600 hover:text-violet-700">
                              <Paperclip className="h-2.5 w-2.5 shrink-0" />
                              <span className="truncate max-w-[160px]">{f.filename || "附件"}</span>
                            </button>
                            <FileUploadDate value={f.uploadedAt} className="block leading-tight" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="dv3-presign-restore-submit"
                  disabled={busy} onClick={restoreOrder}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}{T("恢复工单")}
                </Button>
              </section>
            )}

            {/* 只读时的替代提示：不给入口，只说明为什么 */}
            {readOnly && (
              <section className="rounded-lg border border-slate-200 bg-white p-3 text-[11px] text-slate-500" data-testid="dv3-presign-readonly-hint">
                <p className="inline-flex items-center gap-1.5 font-semibold text-slate-700"><ShieldCheck className="h-4 w-4 text-violet-500" />{T("已签约 · 只读")}</p>
                <p className="mt-1 leading-5">{T("签约前方案、附件与版本记录均已归档，不再提供修改或删除入口。")}</p>
              </section>
            )}

            {/* 工单动作流水：谁在什么时候下达 / 完成 / 退回 / 签约 */}
            <section className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dv3-presign-timeline">
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                <History className="h-4 w-4 text-violet-500" />{T("工单动作流水")}
              </h3>
              {(order.history || []).length === 0 ? (
                <p className="text-[11px] text-slate-400">{T("暂无记录")}</p>
              ) : (
                <ul className="space-y-1 text-[11px] text-slate-600">
                  {order.history.slice().reverse().slice(0, 12).map((h, i) => (
                    <li key={i} className="flex gap-1.5">
                      <span className="shrink-0 tabular-nums text-slate-400">{fmtCN(h.at)}</span>
                      <span className="min-w-0 break-words">
                        {h.action === "dispatch" && <>{T("下达任务")} · {h.by}{h.dueAt ? ` · ${T("要求 {0}", [h.dueAt])}` : ""}</>}
                        {h.action === "complete" && <>{T("标记完成 v{0}", [h.version])} · {h.by}</>}
                        {h.action === "append_files" && <>{T("为 v{0} 增加 {1} 个附件", [h.version, h.files])} · {h.by}</>}
                        {h.action === "rework" && (
                          <>{T("改为待改进")} · {h.by}{h.reason ? `：${h.reason}` : ""}{h.dueAt ? ` · ${T("下一次要求 {0}", [h.dueAt])}` : ""}
                            {(h.fileCount || (h.files || []).length) ? ` · ${T("{0} 个佐证", [h.fileCount || h.files.length])}` : ""}</>
                        )}
                        {h.action === "reassign" && <>{T("改派负责人")}：{h.from || "—"} → {h.to} · {h.by}</>}
                        {h.action === "close" && (
                          <>{T("关闭工单")} · {h.by}{h.reason ? `：${h.reason}` : ""}{h.files ? ` · ${T("{0} 个附件", [h.files])}` : ""}</>
                        )}
                        {h.action === "restore" && <>{T("恢复工单")} · {h.by}{h.toStatus ? ` · ${T("回到 {0}", [h.toStatus])}` : ""}</>}
                        {h.action === "signed" && <>{T("项目已签约，转入只读留痕")} · {h.by || "系统"}</>}
                        {!["dispatch", "complete", "append_files", "rework", "reassign", "close", "restore", "signed"].includes(h.action) && <>{h.action} · {h.by}</>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </aside>
        </div>
      </div>

      {lb && <MediaLightbox tid="dv3-presign-lightbox" items={lb.items} index={lb.index}
        onIndex={(i) => setLb((v) => ({ ...v, index: i }))} onClose={() => setLb(null)} zh={zh} />}
      {completedMode && <CompleteWithFilesDialog api={api} row={order} mode={completedMode}
        onClose={() => setCompletedMode(null)}
        onDone={() => { setCompletedMode(null); load(); onChanged?.(); }} />}
    </div>
  );
}
